/**
 * Stored photo thumbnails (plans/019).
 *
 * A photo has two renditions: the untouched `original` at `photos.file_path`
 * and a small JPEG `thumbnail` beside it. `photos.thumbnail_path` is NULL
 * until the thumbnail object has been uploaded, so a non-null value means the
 * bytes are there to fetch.
 *
 * Thumbnail paths carry the preset version, so changing the preset produces
 * new paths and nothing keyed by path can serve stale bytes:
 *
 *   {organisation_id}/{subject_folder}/{subject_id}/{photo_id}.thumb-v{N}.jpg
 *
 * Keep the shape in sync with the `photos_thumbnail_path_shape` constraint
 * and the manual backfill in `scripts/backfill_photo_thumbnails`.
 */

export type PhotoRendition = "thumbnail" | "original"

export const THUMBNAIL_PRESET = {
  version: 1,
  /** Longest edge in pixels. Smaller images are never upscaled. */
  maxEdge: 640,
  quality: 0.75,
  mimeType: "image/jpeg",
} as const

/** Where a photo's thumbnail lives, derived from its original's path. */
export function thumbnailPathFor(
  originalPath: string,
  photoId: string,
  version: number = THUMBNAIL_PRESET.version,
): string {
  const dir = originalPath.slice(0, originalPath.lastIndexOf("/") + 1)
  return `${dir}${photoId}.thumb-v${version}.jpg`
}

/** Whether a stored thumbnail path was made with the current preset. */
export function isCurrentThumbnailPath(
  thumbnailPath: string | null | undefined,
): boolean {
  return Boolean(
    thumbnailPath?.endsWith(`.thumb-v${THUMBNAIL_PRESET.version}.jpg`),
  )
}

/** Scale (width, height) so the longest edge fits, without upscaling. */
export function thumbnailDimensions(
  width: number,
  height: number,
  maxEdge: number = THUMBNAIL_PRESET.maxEdge,
): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { width, height }
  const scale = maxEdge / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

export class ThumbnailGenerationError extends Error {
  readonly cause: unknown

  constructor(message: string, options?: { cause?: unknown }) {
    super(message)
    this.name = "ThumbnailGenerationError"
    this.cause = options?.cause
  }
}

type DrawSurface = {
  context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D
  toBlob: () => Promise<Blob | null>
  release: () => void
}

function createSurface(width: number, height: number): DrawSurface {
  // OffscreenCanvas.convertToBlob isn't available in every native WebView we
  // ship to, so fall back to a detached <canvas>.
  if (
    typeof OffscreenCanvas !== "undefined" &&
    typeof OffscreenCanvas.prototype.convertToBlob === "function"
  ) {
    const canvas = new OffscreenCanvas(width, height)
    const context = canvas.getContext("2d")
    if (!context) throw new ThumbnailGenerationError("No 2D canvas context")
    return {
      context,
      toBlob: () =>
        canvas.convertToBlob({
          type: THUMBNAIL_PRESET.mimeType,
          quality: THUMBNAIL_PRESET.quality,
        }),
      release: () => {
        canvas.width = 0
        canvas.height = 0
      },
    }
  }
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")
  if (!context) throw new ThumbnailGenerationError("No 2D canvas context")
  return {
    context,
    toBlob: () =>
      new Promise((resolve) =>
        canvas.toBlob(
          resolve,
          THUMBNAIL_PRESET.mimeType,
          THUMBNAIL_PRESET.quality,
        ),
      ),
    // Releasing the backing store promptly matters on iOS, which caps the
    // total canvas memory a page may hold.
    release: () => {
      canvas.width = 0
      canvas.height = 0
    },
  }
}

/**
 * Make the standard thumbnail for an original image.
 *
 * Orientation comes from the image's EXIF so portrait photos stay upright;
 * the output carries no metadata. Transparent inputs are flattened onto
 * white, since JPEG has no alpha. Throws {@link ThumbnailGenerationError}
 * when the browser can't decode the input (HEIC on most non-Apple engines),
 * which callers treat as "no thumbnail" rather than a failed photo.
 */
export async function generateThumbnail(original: Blob): Promise<Blob> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(original, {
      imageOrientation: "from-image",
    })
  } catch (error) {
    throw new ThumbnailGenerationError("Couldn't decode image", {
      cause: error,
    })
  }

  const { width, height } = thumbnailDimensions(bitmap.width, bitmap.height)
  let surface: DrawSurface | null = null
  try {
    surface = createSurface(width, height)
    surface.context.fillStyle = "#ffffff"
    surface.context.fillRect(0, 0, width, height)
    surface.context.imageSmoothingQuality = "high"
    surface.context.drawImage(bitmap, 0, 0, width, height)
    const blob = await surface.toBlob()
    if (!blob || blob.size === 0) {
      throw new ThumbnailGenerationError("Canvas produced no image")
    }
    return blob
  } finally {
    bitmap.close()
    surface?.release()
  }
}
